import mixpanel from 'mixpanel-browser';
import {useEffect, useRef} from 'react';
import {isBotUserAgent} from 'ts-core';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let _initialized = false;
let _embedName = '';
let _logging = false;
let _partnerKey = '';
let _environment = '';

const _deferredCallbacks: Array<() => (() => void) | void> = [];

function _flushDeferred() {
    while (_deferredCallbacks.length > 0) {
        _deferredCallbacks.shift()!();
    }
}

// ---------------------------------------------------------------------------
// Bot detection
// ---------------------------------------------------------------------------

function isBot(): boolean {
    try {
        if (navigator.webdriver) return true;
        return isBotUserAgent(navigator.userAgent);
    } catch {
        return true;
    }
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

export function initMixpanel(
    token: string,
    embedName: string,
    partnerKey: string,
    environment: string,
    logging?: boolean,
) {
    if (_initialized) {
        if (_logging) console.warn('[mixpanel] Already initialized');
        return;
    }
    if (environment !== 'main') {
        console.warn('[mixpanel] Not initialized for non-main environment', environment);
        return;
    }
    if (!token) {
        console.warn('[mixpanel] No token provided — skipping init');
        return;
    }
    if (isBot()) {
        console.warn('[mixpanel] Bot detected — skipping init');
        return;
    }

    _logging = logging ?? (typeof import.meta !== 'undefined' && !!import.meta.env?.DEV);
    _embedName = embedName;
    _partnerKey = partnerKey;
    _environment = environment;

    try {
        mixpanel.init(token, {
            debug: _logging,
            track_pageview: true,
            persistence: 'localStorage',
            disable_persistence: false,
            record_sessions_percent: 10,
            record_heatmap_data: true,
        });

        _initialized = true;

        if (_logging) {
            console.log(`[mixpanel] Initialized for ${embedName}`);
        }
        trackMixpanel('Mixpanel', 'Init');
        _flushDeferred();
    } catch (e) {
        console.error('[mixpanel] Initialization failed', e);
    }
}

// ---------------------------------------------------------------------------
// Event tracking
// ---------------------------------------------------------------------------

export function trackMixpanel(
    eventObject: string,
    eventAction: string,
    eventData: Record<string, unknown> = {},
) {
    if (!_initialized) return;

    try {
        const enrichedData = {
            ...eventData,
            partnerKey: _partnerKey,
            environment: _environment,
            url: window.location.href,
        };

        mixpanel.track(`${_embedName}:${eventObject}:${eventAction}`, enrichedData);

        if (_logging) {
            console.log(`[mixpanel] ${_embedName}:${eventObject}:${eventAction}`, enrichedData);
        }
    } catch (e) {
        console.error('[mixpanel] Tracking failed', e);
    }
}

// ---------------------------------------------------------------------------
// User identity
// ---------------------------------------------------------------------------

export function identifyUser(
    userId: string,
    properties?: Record<string, unknown>,
) {
    if (!_initialized) return;
    try {
        mixpanel.identify(userId);
        if (properties) {
            mixpanel.people.set(properties);
        }
        if (_logging) {
            console.log('[mixpanel] Identified user', userId, properties);
        }
    } catch (e) {
        console.error('[mixpanel] Identify failed', e);
    }
}

export function resetUser() {
    if (!_initialized) return;
    try {
        mixpanel.reset();
        if (_logging) {
            console.log('[mixpanel] User reset');
        }
    } catch (e) {
        console.error('[mixpanel] Reset failed', e);
    }
}

// ---------------------------------------------------------------------------
// Super properties
// ---------------------------------------------------------------------------

export function registerSuperProperties(props: Record<string, unknown>) {
    if (!_initialized) {
        _deferredCallbacks.push(() => registerSuperProperties(props));
        return;
    }
    try {
        mixpanel.register(props);
        if (_logging) {
            console.log('[mixpanel] Super properties registered', props);
        }
    } catch (e) {
        console.error('[mixpanel] Register super properties failed', e);
    }
}

// ---------------------------------------------------------------------------
// Page tracking hook (for SPAs)
// ---------------------------------------------------------------------------

export function useMixpanelPageTracker() {
    const lastPath = useRef<string | null>(null);

    useEffect(() => {
        if (!_initialized) {
            let cancelled = false;
            let cleanup: (() => void) | undefined;
            _deferredCallbacks.push(() => {
                if (!cancelled) cleanup = setup();
            });
            return () => {
                cancelled = true;
                cleanup?.();
            };
        }
        return setup();

        function setup(): (() => void) | undefined {
        const track = () => {
            const path = window.location.pathname + window.location.search;
            if (path !== lastPath.current) {
                lastPath.current = path;
                trackMixpanel('Page', 'View', {
                    path,
                    referrer: document.referrer || '',
                });
            }
        };

        // Track initial page
        track();

        // Listen for popstate (back/forward)
        window.addEventListener('popstate', track);

        // Patch pushState/replaceState
        const originalPush = history.pushState.bind(history);
        const originalReplace = history.replaceState.bind(history);

        history.pushState = (...args: Parameters<typeof history.pushState>) => {
            originalPush(...args);
            track();
        };
        history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
            originalReplace(...args);
            track();
        };

        return () => {
            window.removeEventListener('popstate', track);
            history.pushState = originalPush;
            history.replaceState = originalReplace;
        };
        } // end setup
    }, []);
}

// ---------------------------------------------------------------------------
// Deferred init hook (for map embeds via IntersectionObserver)
// ---------------------------------------------------------------------------

export interface UseMixpanelOptions {
    key: string;
    appContainerRef: React.MutableRefObject<HTMLDivElement | null>;
}

export function useMixpanel({key, appContainerRef}: UseMixpanelOptions) {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const hasInitialized = useRef(false);

    useEffect(() => {
        // Don't re-initialize if already done
        if (hasInitialized.current || !appContainerRef.current) {
            return;
        }

        // Create intersection observer to check if the app container is in viewport
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // If the app container is visible and we haven't initialized yet
                    if (entry.isIntersecting && !hasInitialized.current) {
                        initMixpanel(
                            import.meta.env.VITE_MIXPANEL_TOKEN || '',
                            'Map',
                            key,
                            import.meta.env.VITE_CI_COMMIT_REF_SLUG || '',
                        );
                        hasInitialized.current = true;

                        // Clean up observer after initialization
                        if (observerRef.current) {
                            observerRef.current.disconnect();
                            observerRef.current = null;
                        }
                    }
                });
            },
            {
                // Trigger when any part of the element is visible
                threshold: 0.5,
                // Use viewport as root
                root: null,
                rootMargin: '0px',
            },
        );

        // Start observing the app container
        observerRef.current.observe(appContainerRef.current);

        // Cleanup function
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        };
    }, [key, appContainerRef]);
}
